/**
 * pickInitialViewport — which camera a freshly-shown board should start from,
 * given the active tab and the persisted overview camera.
 *
 * The single board surface backs both board tabs and file tabs (editor + board
 * split). A board tab seeds from the overview camera; a FILE tab must NOT —
 * its board lives in a different-sized pane (beside/above the editor), so an
 * inherited overview camera frames the wrong region and the board looks empty.
 * Returning `undefined` lets the board auto-fit to its own pane; the fit then
 * persists per tab and is restored on the next visit.
 */
import type { Tab } from "./tabs";
import type { BoardViewport } from "./WorkspaceStore";

export function pickInitialViewport(
  tab: Tab | undefined,
  overview: BoardViewport | undefined,
): BoardViewport | undefined {
  if (!tab) return overview;
  // EVERY tab kind that carries a remembered camera restores it — the board
  // surface writes `viewport` onto whichever tab is active (view and doc tabs
  // included), so dropping it here silently discarded those tabs' pan/zoom.
  const own = (tab.state as { readonly viewport?: BoardViewport }).viewport;
  if (tab.kind === "board") return own ?? overview;
  // Non-board tabs: own camera if remembered, else auto-fit (undefined) — the
  // board shares a split with an editor there, so an inherited overview camera
  // frames the wrong region and the board looks empty.
  return own ?? undefined;
}
